/**
 * htmlParser.ts
 * Utility functions for parsing HTML content in exam questions
 * Supports LaTeX formulas, images, and rich text content
 */

import DOMPurify from 'dompurify';

// Note: parseFormulas is imported from the app that uses this utility
// This file provides HTML parsing utilities that work with parsed formulas

/**
 * Get the backend base URL based on current environment
 */
function getBackendBaseUrl(): string {
  // Development environment
  if (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1') {
    return 'http://localhost:3001';
  }

  // Production: Use current domain (nginx will proxy to backend)
  return window.location.origin;
}

/**
 * Transform image URLs from localhost to production URLs
 * This fixes the issue where exam content created in dev has hardcoded localhost:3001 URLs
 * Also transforms cross-domain URLs (e.g., portal domain -> current domain)
 */
export function transformImageUrls(htmlContent: string): string {
  if (!htmlContent) return '';

  const backendUrl = getBackendBaseUrl();
  const currentOrigin = window.location.origin;

  // Replace localhost:3001 and localhost:3001/api URLs with the correct backend URL
  // Also replace any absolute URLs from other domains (portal, staging, prod) with current domain
  // This handles cases where content was created from one app (portal) but viewed from another (thpt)
  return (
    htmlContent
      .replace(/http:\/\/localhost:3001\/api/g, `${backendUrl}/api`)
      .replace(/http:\/\/localhost:3001/g, backendUrl)
      .replace(/http:\/\/127\.0\.0\.1:3001\/api/g, `${backendUrl}/api`)
      .replace(/http:\/\/127\.0\.0\.1:3001/g, backendUrl)
      // Replace portal domain with current domain
      .replace(/https?:\/\/portal-vsat-exam-stg\.finative\.tech\/api/g, `${currentOrigin}/api`)
      .replace(/https?:\/\/thpt-exam-stg\.finative\.tech\/api/g, `${currentOrigin}/api`)
      .replace(/https?:\/\/portal\.thitrenmaytinh\.edu\.vn\/api/g, `${currentOrigin}/api`)
      .replace(/https?:\/\/thpt\.thitrenmaytinh\.edu\.vn\/api/g, `${currentOrigin}/api`)
  );
}

/**
 * Parse HTML content and render it safely
 * Supports LaTeX formulas, images, and rich text
 */
export function parseHtmlContent(htmlContent: string): string {
  if (!htmlContent) return '';

  // Transform localhost URLs to production URLs
  const transformedContent = transformImageUrls(htmlContent);

  // Sanitize HTML to prevent XSS attacks
  return DOMPurify.sanitize(transformedContent, {
    ALLOWED_TAGS: [
      'p',
      'div',
      'span',
      'br',
      'hr',
      'h1',
      'h2',
      'h3',
      'h4',
      'h5',
      'h6',
      'strong',
      'b',
      'em',
      'i',
      'u',
      's',
      'sub',
      'sup',
      'ul',
      'ol',
      'li',
      'table',
      'thead',
      'tbody',
      'tfoot',
      'tr',
      'th',
      'td',
      'caption',
      'colgroup',
      'col',
      'img',
      'figure',
      'figcaption',
      'blockquote',
      'pre',
      'code',
      'a',
    ],
    ALLOWED_ATTR: [
      'src',
      'alt',
      'width',
      'height',
      'title',
      'href',
      'target',
      'rel',
      'class',
      'style',
      'id',
      'colspan',
      'rowspan',
      'scope',
      'data-type',
      'data-latex',
      'data-value',
    ],
    ALLOW_DATA_ATTR: true,
  });
}

/**
 * Extract text content from HTML (for search and indexing)
 */
export function extractTextFromHtml(htmlContent: string): string {
  if (!htmlContent) return '';

  // Remove HTML tags and decode entities
  const text = htmlContent
    .replace(/<[^>]*>/g, '') // Remove HTML tags
    .replace(/&nbsp;/g, ' ') // Replace non-breaking spaces
    .replace(/&amp;/g, '&') // Decode ampersands
    .replace(/&lt;/g, '<') // Decode less than
    .replace(/&gt;/g, '>') // Decode greater than
    .replace(/&quot;/g, '"') // Decode quotes
    .replace(/&#39;/g, "'") // Decode apostrophes
    .trim();

  return text;
}

/**
 * Check if HTML content contains LaTeX formulas
 */
export function containsLatex(htmlContent: string): boolean {
  return /\\[a-zA-Z]|\\\(|\\\[|\$/.test(htmlContent);
}

/**
 * Check if HTML content contains images
 */
export function containsImages(htmlContent: string): boolean {
  return /<img[^>]*>/i.test(htmlContent);
}

/**
 * Extract image URLs from HTML content
 */
export function extractImageUrls(htmlContent: string): string[] {
  const imgRegex = /<img[^>]+src=["']([^"']+)["'][^>]*>/gi;
  const urls: string[] = [];
  let match;

  while ((match = imgRegex.exec(htmlContent)) !== null) {
    urls.push(match[1]);
  }

  return urls;
}

/**
 * Sanitize HTML content for security
 */
export function sanitizeHtml(htmlContent: string): string {
  // Basic HTML sanitization - in production, use a proper HTML sanitizer like DOMPurify
  return htmlContent
    .replace(/<script[^>]*>.*?<\/script>/gi, '') // Remove script tags
    .replace(/<iframe[^>]*>.*?<\/iframe>/gi, '') // Remove iframe tags
    .replace(/on\w+="[^"]*"/gi, '') // Remove event handlers
    .replace(/javascript:/gi, ''); // Remove javascript: URLs
}
